import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormProvider, useForm, useFieldArray } from 'react-hook-form'
import { Box, Divider, Typography } from '@mui/material'
import GenericModal from '../../../../components/GenericModal'
import Search from '../../../../components/Search'
import FilterPanel from './components/FilterPanel'
import CenteredLoader from './components/CenteredLoader'
import { createBulkAddCourseSection, fetchCourseSectionHeaders, toFilterableRows, toSlotSectionFilterRows } from '../../../../APIManager/BulkAddCourseSectionManger'
import { useMutation, useQuery } from '@tanstack/react-query'
import BulkAddCourseSections from './Table'
import { notification } from '../../../../components/Notifications'
import { buildBulkAddSectionPayload, type BulkAddSectionDateValues } from '../../../../helpers/BulkAddCourseSection'
import { useSlotTaskContext } from '../../../../contexts/SlotTaskContext'
import { isBulkReopenScheduleEnabled, startBulkGoLive, startBulkReopen, type BulkSectionStatusSelection, type ReopenTargetStatus } from '../bulkSectionStatusActions'
import { formatReopenDateError, readReopenDateError } from '../reopenDateError'
import { yupResolver } from '@hookform/resolvers/yup'
import BulkReopenConfirm, { buildValidationSchema, type BulkReopenDates, type BulkReopenFormValues } from './BulkReopenConfirm'
import BulkReopenModeChoice from './BulkReopenModeChoice'
import BulkGoLiveConfirm from './BulkGoLiveConfirm'

interface BulkExportModalProps {
  isOpen: boolean
  onClose: () => void
  onParentClose?: () => void
  slotId?: string
  termId?: string
  /** Survey dates from the parent Add Course Section modal. */
  sectionDates?: BulkAddSectionDateValues
  isRollingRelease?: boolean
  /** Bulk Update mode — list only already-attached sections and send isPresent: true. */
  isPresent?: boolean
  /** Resolved opt-out tri-state (undefined = leave unchanged; true = out; false = in). */
  isOptedOut?: boolean
  /**
   * Bulk Reopen mode — select closed sections, then collect one shared set of reopen dates in the
   * confirm step. Reads the slot's own section list with `view=closed` rather than the catalog, and
   * does not go through the bulk-add payload: reopening is a status change, not a date edit.
   * `isPresent` is not needed with it — slot nodes are attached by definition.
   */
  isReopen?: boolean
  /**
   * Bulk Go Live mode — select published (upcoming) sections and start them. Lists the slot's own
   * sections with `view=published`, the only status go-live accepts, and needs no dates: the
   * section already carries its own.
   */
  isGoLive?: boolean
  /**
   * Reopen / go-live modes — whether this Survey Project has opt-out switched on
   * (`isAcademicUnitOptOutEnabled || isInstructorOptOutEnabled`, the backend's own
   * `is_opt_out_setting_enabled`). It decides whether opted-out sections are excluded, and must be
   * the same value for the listing and the submit, or the batch acts on a different set of
   * sections than the admin saw.
   */
  isOptOutSettingEnabled?: boolean
  /** Reopen / go-live modes — refresh the section table once the task lands. */
  onCompleted?: () => void
}

/** Represents a single row in the filter form managed by react-hook-form. */
interface FilterCondition {
  category: string
  operator: string
  date: string
}

/** The API-ready shape of a filter sent in the bulk-add payload. */
interface Filter {
  field: string
  op?: string
  value: string | string[]
}

const BulkSelectCourseSectionModal: React.FC<BulkExportModalProps> = ({
  isOpen,
  onClose,
  onParentClose,
  slotId,
  termId,
  sectionDates,
  isRollingRelease = false,
  isPresent,
  isOptedOut,
  isReopen,
  isGoLive,
  isOptOutSettingEnabled,
  onCompleted
}) => {
  // --- Filter & search state ---
  // filterState combines the active filter payload and a monotonic counter
  // into a single state object so both always update atomically in one render.
  // This prevents TanStack Query from firing with a new trigger but stale
  // filters when the two values were previously stored as separate states.
  const [filterState, setFilterState] = useState<{ data: Filter[] | undefined, trigger: number }>({ data: undefined, trigger: 0 })
  const [searchInput, setSearchInput] = React.useState('') // Raw keystroke value — updates instantly for responsive UX
  const [searchValue, setSearchValue] = React.useState('') // Debounced copy — drives the actual CDG API query

  // --- Selection state (three-way model) ---
  // The save payload supports three mutually exclusive modes:
  //   1. Manual pick  → send `selectedRowsData` (array of sectionIds)
  //   2. Select All   → send `selectAll: true` with no exclusions
  //   3. Select All minus exclusions → send `selectAll: true` + `unselectedIds`
  const [selectedRowsData, setSelectedRowsData] = useState<string[] | undefined>()
  const [selectAll, setSelectAll] = useState(false)
  const [unselectedIds, setUnselectedIds] = useState<string[]>([])
  const [matchedTotal, setMatchedTotal] = useState(0)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  /**
   * Which branch of the reopen fork the admin picked, or `null` while the fork itself is showing.
   *
   * Reopen is a three-step flow — select, choose, dates — and this is step two's answer. It is not
   * part of the selection state on purpose: stepping back to the fork, or out to the list, leaves
   * the selection untouched, including a Select All that carries exclusions rather than ids.
   */
  const [reopenMode, setReopenMode] = useState<ReopenTargetStatus | null>(null)
  /**
   * A date refusal from the reopen endpoint, kept so the confirm step can show it in place.
   *
   * The bulk endpoint validates the shared dates on the request (Backend #2164) rather than once
   * per section, so this arrives while the admin still has the date picker open. Reporting it as a
   * toast and closing the modal would make them reopen it, re-select, and guess.
   */
  const [statusSubmitError, setStatusSubmitError] = useState<string | null>(null)
  /** Closes in the same tick as the first click — see `submitStatusChange`. */
  const isSubmittingRef = useRef(false)

  /**
   * The reopen dates, owned here so they outlive step 3.
   *
   * The step unmounts when the admin steps back to the fork, which is what keeps the *selection*
   * cheap to return to. It used to take the dates with it: fill an end date and both release dates
   * on Go Live, realise you meant to schedule, press Back, and all three were gone — even though
   * those fields are identical in both branches, and the button's own copy promises the trip is
   * cheap. Found by review.
   *
   * The resolver reads the mode through a ref rather than being rebuilt per mode, so the form
   * instance itself never changes. `useForm` captures its resolver once, and swapping schemas would
   * mean recreating the form — which is the thing that lost the values. Same shape the distribution
   * form uses for `lastChangedField`.
   */
  const reopenModeRef = useRef<ReopenTargetStatus>('live')
  reopenModeRef.current = reopenMode ?? 'live'
  const reopenResolver = useCallback(
    async (values: any, context: any, options: any) =>
      await yupResolver(buildValidationSchema(reopenModeRef.current))(values, context, options),
    []
  )
  const reopenFormMethods = useForm<BulkReopenFormValues>({
    resolver: reopenResolver,
    defaultValues: {
      startTimestamp: null,
      endTimestamp: null,
      earlyAccessReleaseTimestamp1: null,
      earlyAccessReleaseTimestamp2: null
    }
  })

  /**
   * Leaving the confirm step, from any of the ways out.
   *
   * One function because the state a dismissal has to reset kept growing — first `isConfirmOpen`,
   * then the fork's `reopenMode`, then a refusal to display — and each exit reset the subset that
   * existed when it was written. The dialog's own close control kept a refusal on screen that
   * Back had already learned to clear, so re-entering showed a stale error over a blank form.
   * Found by review.
   */
  const closeStatusConfirm = useCallback((): void => {
    setIsConfirmOpen(false)
    setReopenMode(null)
    setStatusSubmitError(null)
    // Dropped on the way *out*, not on the way back to the fork. Returning to the fork is a step
    // within the same task and keeps what was typed; closing the modal ends the task.
    reopenFormMethods.reset()
  }, [reopenFormMethods])

  const { setTask, checkIfBlocked } = useSlotTaskContext()
  const isReopenMode = isReopen === true
  const isGoLiveMode = isGoLive === true
  // Both status modes list the slot's own sections, so neither may take the bulk-add update path,
  // which edits dates and opt-out through `bulk_add_sections`.
  const isUpdateMode = isPresent === true && !isReopenMode && !isGoLiveMode
  const isStatusMode = isReopenMode || isGoLiveMode
  const [isStatusSubmitting, setIsStatusSubmitting] = useState(false)

  const selectionCount = selectAll
    ? Math.max(0, matchedTotal - unselectedIds.length)
    : (selectedRowsData?.length ?? 0)

  // Debounce the raw search input by 400 ms before updating the value
  // that the CDG query watches.  This prevents a network request on every
  // keystroke while still feeling responsive because the text field itself
  // updates immediately via `searchInput`.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(searchInput)
    }, 400)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchInput])

  // CDG header metadata (field names, display labels, data types).
  // Cached for 5 min because headers change rarely.  The filter panel
  // consumes this to populate its field dropdown and derive type-aware operators.
  const { data: filterHeaderRows = [], isLoading: isFilterHeaderLoading, error: filterHeaderError } = useQuery({
    queryKey: ['cdg_headers'],
    queryFn: fetchCourseSectionHeaders,
    staleTime: 5 * 60 * 1000
  })

  const methods = useForm<{ filters?: FilterCondition[] }>({
    defaultValues: {
      filters: []
    }
  })

  const { control, handleSubmit } = methods
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'filters'
  })

  const { trigger, getValues } = methods
  const handleAddCondition = useCallback(async () => {
    const filters = getValues('filters') || []
    const fieldsToValidate = filters.flatMap((_, i) => [
      `filters.${i}.category`,
      `filters.${i}.operator`,
      `filters.${i}.date`
    ])
    const valid = await trigger(fieldsToValidate as Parameters<typeof trigger>[0])
    if (valid) {
      append({ category: '', operator: '', date: '' })
    }
  }, [append, getValues, trigger])

  // Transform the form-level filter objects into the API-ready shape
  // ({ field, op, value }) and bump the trigger counter so the CDG
  // query re-fires.  Incomplete rows (user left a field blank) are
  // silently stripped so the API never receives a partial filter.
  // Both the filter payload and the trigger are updated in a single
  // setState call so they always land in the same render, preventing
  // TanStack Query from firing with an incremented trigger but stale filters.
  const handleFilter = useCallback((data: { filters?: FilterCondition[] }) => {
    const transformed = (data.filters || [])
      .filter(filter => filter.category && filter.operator && filter.date)
      .map(filter => ({
        field: filter.category,
        op: filter.operator,
        value: filter.date
      }))
    setFilterState(prev => ({
      data: transformed.length > 0 ? transformed : undefined,
      trigger: prev.trigger + 1
    }))
  }, [])

  const { mutateAsync, isPending: bulkAddCourseSectionPending } = useMutation({
    mutationFn: async ({ slotId, data }: { slotId: string, data: any }) => {
      return await createBulkAddCourseSection(slotId, data)
    }
  })

  const handleBack = useCallback(() => {
    onClose()
  }, [onClose])

  const handleRemoveFilter = useCallback((index: number) => { remove(index) }, [remove])

  // Build the final payload and submit.  `buildBulkAddSectionPayload`
  // merges the user-defined filters and selection model with survey
  // dates from the parent Add Course Section modal.
  const submitBulkRequest = async (): Promise<void> => {
    if (await checkIfBlocked()) return

    try {
      const data = buildBulkAddSectionPayload(
        selectAll,
        unselectedIds,
        selectedRowsData,
        filterState.data,
        sectionDates,
        isRollingRelease,
        isUpdateMode ? { isPresent: true, isOptedOut } : undefined
      )
      const response = await mutateAsync({
        slotId: slotId || '',
        data
      })

      const taskIdFromResponse = response?.data?.taskId
      if (!taskIdFromResponse) {
        notification('Task ID not received from server', { variant: 'error' })
        return
      }

      if (isUpdateMode) {
        setTask(taskIdFromResponse, response?.data?.message ?? 'Updating course sections...', 'Course sections updated successfully.')
        notification('Course section update task has been submitted successfully', { variant: 'success' })
      } else {
        setTask(taskIdFromResponse, response?.data?.message ?? 'Adding course sections...', 'Course sections added successfully.')
        notification('Course section addition task has been submitted successfully', { variant: 'success' })
      }
      setIsConfirmOpen(false)
      onClose()
      // Close parent modal if callback provided
      if (onParentClose) {
        onParentClose()
      }
    } catch (error: any) {
      notification(error?.message ?? (isUpdateMode ? 'Failed to update course sections' : 'Failed to add course sections'), { variant: 'error' })
    }
  }

  /**
   * Submit for both status modes. Goes nowhere near `buildBulkAddSectionPayload`: reopening and
   * going live are status changes with their own per-section rules, not date edits, so each posts
   * to its own bulk endpoint.
   *
   * One request, and then it is a background task like every other bulk action on this table — the
   * selection is resolved server-side from the same filters the listing used, and the per-section
   * outcomes come back in the task result. Partial success is the expected shape of a successful
   * run, not an error: reopen validates one shared end date against each section's own boundaries,
   * and go-live refuses opted-out sections. `SlotTaskContext` reports that when the task lands.
   */
  const submitStatusChange = async (dates?: BulkReopenDates): Promise<void> => {
    // Re-entry guard, and a ref rather than the `isStatusSubmitting` state beside it.
    //
    // `GenericButton` renders its loading state as an icon button that keeps `type="submit"` and
    // sets `disabled` only if it was passed one, so a second click during the POST submits the form
    // again — and Enter would do it even with the button disabled. The task is not registered until
    // the response returns, so `checkIfBlocked` cannot see the first one either: two batches go out
    // over the same selection. On the publish branch the second finds every section already
    // PUBLISHED and reports `wrong_status`, so a run that succeeded is reported as a failure.
    //
    // State cannot hold this line: two clicks in one tick both read `isStatusSubmitting` before
    // React re-renders, and there is an `await` before it would be set. The ref closes in the same
    // tick as the first click. Found by review; the state-flag version still let both through.
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true
    if (await checkIfBlocked()) {
      isSubmittingRef.current = false
      return
    }
    setStatusSubmitError(null)
    setIsStatusSubmitting(true)
    try {
      const selection: BulkSectionStatusSelection = {
        filters: filterState.data,
        search: searchValue || undefined,
        selectAll,
        selectedIds: selectedRowsData,
        unselectedIds,
        isOptOutSettingEnabled
      }
      const isScheduledReopen = isReopenMode && reopenMode === 'published'
      const task = isGoLiveMode
        ? await startBulkGoLive(slotId ?? '', selection)
        : await startBulkReopen(slotId ?? '', selection, dates as BulkReopenDates, reopenMode ?? 'live')

      if (!task) {
        notification('Task ID not received from server', { variant: 'error' })
        return
      }

      // The kind decides the completion toast's voice and is persisted with the task, so a
      // scheduled batch cannot be reported as collecting once this page is gone.
      const taskKind = isGoLiveMode
        ? 'bulkGoLive'
        : isScheduledReopen ? 'bulkReopenScheduled' : 'bulkReopen'
      setTask(
        task.taskId,
        task.message ?? (isGoLiveMode
          ? 'Taking course section surveys live...'
          : isScheduledReopen ? 'Scheduling course section surveys...' : 'Reopening course section surveys...'),
        isGoLiveMode
          ? 'Course section surveys are now live.'
          : isScheduledReopen ? 'Course section surveys scheduled.' : 'Course section surveys reopened.',
        onCompleted,
        taskKind
      )
      notification(
        isGoLiveMode
          ? 'Bulk go live has been submitted successfully'
          : isScheduledReopen
            ? 'Bulk schedule has been submitted successfully'
            : 'Bulk reopen has been submitted successfully',
        { variant: 'success' }
      )
      closeStatusConfirm()
      onClose()
      onParentClose?.()
    } catch (error: any) {
      // A refused date is not a failed submission — it is the admin being told which dates are
      // allowed, with the picker still in front of them. Shown in place, modal left open. Anything
      // else (permissions, a project that is not Live, the network) keeps the toast it had.
      const dateError = readReopenDateError(error?.data)
      if (dateError !== null) {
        setStatusSubmitError(formatReopenDateError(dateError))
        return
      }
      notification(
        error?.message ?? (isGoLiveMode ? 'Failed to take course sections live' : 'Failed to reopen course section surveys'),
        { variant: 'error' }
      )
    } finally {
      isSubmittingRef.current = false
      setIsStatusSubmitting(false)
    }
  }

  // The header metadata describes the Section catalog. The status modes read the slot's own
  // section list, which knows a smaller set of filter keys and silently discards the rest, so they
  // are offered only the fields that endpoint can actually apply.
  //
  // Add mode queries the catalog itself, so there the catalog's own `allowFilter` is the answer —
  // the rows carry every key, filterable or not. The status whitelist already lists only keys the
  // slot endpoint can filter, so it does not need the flag on top: that flag describes the catalog,
  // not the endpoint those modes actually query.
  const availableFilterRows = isStatusMode ? toSlotSectionFilterRows(filterHeaderRows) : toFilterableRows(filterHeaderRows)

  // Update and the status modes confirm blast radius before submitting; create saves directly.
  const handleSave = async (): Promise<void> => {
    if (isUpdateMode || isStatusMode) {
      setIsConfirmOpen(true)
      return
    }
    await submitBulkRequest()
  }

  // Save is enabled when the user has selected at least one section — either
  // explicitly (selectedRowsData has entries) or implicitly (selectAll is true).
  const isSaveDisabled = !selectAll && (!selectedRowsData || (Array.isArray(selectedRowsData) && selectedRowsData?.length === 0))
  const isLoading = isFilterHeaderLoading

  return (
    <>
      <GenericModal
        title={isGoLiveMode
          ? 'Bulk Go Live'
          : isReopenMode
            ? 'Bulk Reopen Course Section Surveys'
            : isUpdateMode ? 'Bulk Update Course Sections' : 'Add Course Section to Survey Project'}
        isOpen={isOpen}
        onClose={onClose}
        maxWidth="md"
        destructiveButtonText="Back"
        // Reopen has a second step that still needs the dates, so this button only advances —
        // calling it Save would claim a write that has not happened. "Reopen" on the confirm
        // dialog is the commit.
        confirmActionText={isStatusMode ? 'Next' : 'Save'}
        onConfirm={handleSave}
        confirmButtonDisabled={isSaveDisabled}
        confirmButtonLoading={!isUpdateMode && bulkAddCourseSectionPending}
      >
        <Divider sx={{ mb: 3 }} />
        <FormProvider {...methods}>
          <Box>
            {isLoading
              ? (
                <CenteredLoader minHeight={200} />
                )
              : (
                <FilterPanel
                  fields={fields}
                  methods={methods}
                  handleBack={handleBack}
                  handleAddCondition={() => { void handleAddCondition() }}
                  handleRemoveFilter={handleRemoveFilter}
                  handleFilter={() => { void handleSubmit(handleFilter)() }}
                  filterRows={availableFilterRows}
                  filterRowsLoading={isLoading}
                  filterRowsError={filterHeaderError}
                />
                )}
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box display="flex" alignItems="center" gap={2} mt={3} mb={3}>
            <Search
              placeholder="Search Filtered Results"
              fillContainer={true}
              onSearch={setSearchInput}
              onChange={setSearchInput}
              disabled={filterState.trigger === 0}
            />
          </Box>
          <BulkAddCourseSections
            searchValue={searchValue}
            filters={filterState.data}
            termId={termId}
            slotId={slotId}
            setSelectedRowsData={setSelectedRowsData}
            setSelectAll={setSelectAll}
            setUnselectedIds={setUnselectedIds}
            setAllRowIds={() => {}}
            filterTrigger={filterState.trigger}
            isPresent={isPresent}
            listSource={isGoLiveMode ? 'publishedSlotSections' : isReopenMode ? 'closedSlotSections' : 'catalog'}
            isOptOutSettingEnabled={isOptOutSettingEnabled}
            onMatchedCountChange={setMatchedTotal}
          />
        </FormProvider>
      </GenericModal>

      {isUpdateMode && (
        <GenericModal
          title="Confirm Bulk Update"
          isOpen={isConfirmOpen}
          onClose={() => { setIsConfirmOpen(false) }}
          maxWidth="sm"
          confirmActionText="Update"
          onConfirm={submitBulkRequest}
          confirmButtonLoading={bulkAddCourseSectionPending}
          destructiveButtonText="Cancel"
        >
          <Typography>
            Update {selectionCount.toLocaleString()} course section{selectionCount === 1 ? '' : 's'}?
            Only the fields you set will change; blank dates and &quot;Don&apos;t change&quot; opt-out leave existing values alone.
          </Typography>
        </GenericModal>
      )}

      {isGoLiveMode && (
        <GenericModal
          title="Confirm Bulk Go Live"
          isOpen={isConfirmOpen}
          onClose={() => { setIsConfirmOpen(false) }}
          maxWidth="sm"
          action={false}
        >
          <BulkGoLiveConfirm
            selectionCount={selectionCount}
            onConfirm={async () => { await submitStatusChange() }}
            onCancel={closeStatusConfirm}
            isSubmitting={isStatusSubmitting}
          />
        </GenericModal>
      )}

      {/* Step 2 of the reopen flow. Only when the publish branch is available: with the choice
          switched off there is nothing to choose, so the fork would be a step that always has the
          same answer. */}
      {isReopenMode && isBulkReopenScheduleEnabled() && reopenMode === null && (
        <GenericModal
          title="Reopen Course Section Surveys"
          isOpen={isConfirmOpen}
          onClose={closeStatusConfirm}
          maxWidth="sm"
          action={false}
        >
          <BulkReopenModeChoice
            selectionCount={selectionCount}
            onChoose={setReopenMode}
            onBack={closeStatusConfirm}
          />
        </GenericModal>
      )}

      {/* Step 3, and the whole of the flow while the publish branch is off. */}
      {isReopenMode && (!isBulkReopenScheduleEnabled() || reopenMode !== null) && (
        <GenericModal
          title={reopenMode === 'published' ? 'Reopen & Publish' : 'Confirm Bulk Reopen'}
          isOpen={isConfirmOpen}
          onClose={closeStatusConfirm}
          maxWidth="sm"
          action={false}
        >
          <BulkReopenConfirm
            selectionCount={selectionCount}
            mode={reopenMode ?? 'live'}
            methods={reopenFormMethods}
            submitError={statusSubmitError}
            onConfirm={submitStatusChange}
            // Back to the fork when there is one to go back to, otherwise out to the selection —
            // either way the selection survives, since it lives in the modal behind this one.
            onCancel={() => {
              // Back to the fork when there is one to go back to — the selection survives either
              // way, since it lives in the modal behind this one. A refusal never outlives the
              // step it belongs to.
              setStatusSubmitError(null)
              if (isBulkReopenScheduleEnabled()) {
                setReopenMode(null)
                return
              }
              closeStatusConfirm()
            }}
            isSubmitting={isStatusSubmitting}
          />
        </GenericModal>
      )}

    </>
  )
}

export default BulkSelectCourseSectionModal
